/**
 * Preview-only 12 October 2026 title-tag overrides for static Integrated.Social routes.
 * Blog titles and their dateModified values remain in post data so SSR, client metadata
 * and BlogPosting JSON-LD derive from the same record.
 */

export const TITLE_FIX_DATE = "2026-10-12";

export const STATIC_TITLE_FIXES: Readonly<Record<string, string>> =
  Object.freeze({
    "/services/google-ai-mode-optimisation/":
      "Google AI Mode Optimization Agency London: B2B",
    "/services/ai-governance/": "AI Governance Agency: EU AI Act and GDPR for B2B",
    "/services/full-ai-growth-engine/":
      "Full AI Growth Engine: Digital Marketing, SW London & Surrey",
    "/services/local-presence-plan/":
      "Local Presence Plan: SW London & Surrey Near Me | £299/mo",
    "/services/geo-agency-london/":
      "GEO Agency London | Generative Engine Optimization for B2B",
    "/local/restaurant-marketing-richmond-kingston-guildford/":
      "AI Marketing for Restaurants: Richmond, Kingston, Guildford",
    "/local/gym-marketing-putney-wimbledon-kingston/":
      "AI Marketing for Gyms in Putney, Wimbledon and Kingston",
    "/local/salon-beauty-marketing-sw-london-surrey/":
      "AI Marketing for Hair Salons & Beauty in SW London & Surrey",
    "/local/pilates-studio-marketing-sw-london-surrey/":
      "AI Marketing for Pilates Studios in SW London and Surrey",
    "/services/performance-max-agency/":
      "Performance Max Agency London | Google Shopping & PMax",
    "/services/ppc-performance-max/":
      "PPC Agency London | Google Ads for B2B and B2C Brands",
    "/local/law-firm-solicitor-marketing-sw-london-surrey/":
      "AI Marketing for Law Firms & Solicitors, SW London & Surrey",
    "/local/cafe-marketing-richmond-putney-wimbledon/":
      "AI Marketing for Cafés in Richmond, Putney and Wimbledon",
    "/tools/ai-token-calculator/":
      "Free AI Token Calculator | Verified API Pricing",
    "/case-studies/agentic-ai-gtm/":
      "Agentic AI GTM Case Study: 14 Agents, 5 Personas",
    "/services/ai-training-for-leaders/":
      "AI Training for Business Leaders & Executives in London",
    "/about/": "About Integrated.Social | AI-First Marketing Agency",
    "/services/ai-visibility-plan/":
      "AI Visibility Plan: AI Website & Ads, SW London | £599/mo",
    "/local/clinic-medical-dental-marketing-sw-london/":
      "AI Marketing for Clinics, Dental & Physio in SW London",
    "/local/tyre-mot-garage-marketing-sw-london-surrey/":
      "AI Marketing for Garages, MOT & Tyres in SW London",
    "/pricing/": "Flexible AI Marketing Starting Points | Integrated.Social",
    "/services/gemini-agentic-ai/":
      "Agentic AI Marketing Agency London | AI Lead Generation",
    "/services/preferred-sources-strategy/":
      "AI Preferred Sources Strategy: Easier for ChatGPT to Cite",
    "/case-studies/":
      "AI Marketing Case Studies: AEO, PPC & Agentic AI Results",
    "/faq/": "AI Marketing FAQ: AEO, Agentic AI & B2B Growth Questions",
    "/ai-marketing-news/":
      "AI Marketing News: Agentic AI, Governance & AI Search",
    "/services/aeo-agency-london/":
      "AEO Agency London | Answer Engine Optimisation Agency",
    "/case-studies/destination-marketing/":
      "Destination Marketing Case Study: 4.8x ROAS for Tourism",
    "/services/integrated-marketing-communications/":
      "Integrated Marketing Communications Agency London | IMC",
    "/free-ai-growth-audit/":
      "Free AI Visibility Score for Websites | Integrated.Social",
    "/aeo/": "AEO Hub: Answer Engine Optimisation Guides for B2B Teams",
    "/methodology/":
      "Our Methodology | How Integrated.Social Builds AI Marketing",
    "/services/ai-marketing-agency-london/":
      "AI Marketing Agency London | B2B AI Growth Marketing",
    "/services/seo-aeo-geo/":
      "AEO Agency | SEO and AEO Agency for B2B, with GEO",
    "/services/ai-citation-service/":
      "Get Cited by ChatGPT & Gemini | AI Citation Service",
    "/services/ai-marketing-strategy/":
      "AI Marketing Strategy UK | Persona-Led GTM",
    "/modi-elnadi/": "Modi Elnadi | Paid and Earned AI Growth | London",
    "/case-studies/fintech-persona-targeting/":
      "UK FinTech Case Study: +350% Registrations via Personas",
    "/editorial-policy/": "Editorial Policy | Integrated.Social",
    "/case-studies/b2b-saas-abm/":
      "B2B SaaS ABM Case Study: +312% Pipeline Growth in 90 Days",
  });

// Routes subsequently revised after the 12 October bulk title release keep
// their own meaningful metadata revision date rather than inheriting it.
export const STATIC_TITLE_FIX_DATES: Readonly<Record<string, string>> =
  Object.freeze({
    "/case-studies/destination-marketing/": "2026-11-04",
    "/privacy-policy/": "2026-11-03",
    "/cookie-policy/": "2026-11-03",
    "/tools/ai-token-calculator/": "2026-11-03",
    "/resources/ai-tools/": "2026-11-03",
    "/services/google-ai-mode-optimisation/": "2026-11-02",
    "/free-ai-growth-audit/": "2026-10-21",
    "/aeo/": "2026-10-20",
    "/pricing/": "2026-10-20",
    "/services/aeo-agency-london/": "2026-11-04",
    "/services/performance-max-agency/": "2026-11-02",
    "/services/ppc-performance-max/": "2026-11-02",
    "/services/ai-marketing-agency-london/": "2026-11-02",
    "/services/account-based-marketing/": "2026-11-04",
    "/services/integrated-marketing-communications/": "2026-11-04",
    "/services/geo-agency-london/": "2026-11-02",
    "/services/gemini-agentic-ai/": "2026-11-02",
    "/services/ai-governance/": "2026-11-02",
    "/services/preferred-sources-strategy/": "2026-10-20",
    "/services/seo-aeo-geo/": "2026-11-04",
    "/services/ai-citation-service/": "2026-10-20",
    "/services/ai-marketing-strategy/": "2026-11-02",
    "/modi-elnadi/": "2026-11-03",
    "/ai-marketing-news/": "2026-11-03",
  });

function normalisePath(canonical: string): string {
  const url = new URL(canonical, "https://integrated.social");
  return url.pathname.endsWith("/") ? url.pathname : `${url.pathname}/`;
}

export function resolveStaticTitleFix(
  canonical: string,
  fallbackTitle: string
): string {
  return STATIC_TITLE_FIXES[normalisePath(canonical)] ?? fallbackTitle;
}

export function getStaticTitleFixDateModified(
  canonical: string
): string | undefined {
  const pathname = normalisePath(canonical);
  return (
    STATIC_TITLE_FIX_DATES[pathname] ??
    (STATIC_TITLE_FIXES[pathname] ? TITLE_FIX_DATE : undefined)
  );
}
